import { CSSProperties } from "react";
import { FaPlus } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

import { Currency } from "@bentolabs/kit/entities";
import { useBalanceQuery } from "@bentolabs/kit/hooks";
import { cn } from "@bentolabs/kit/utils";
import { useAccount } from "wagmi";

import { useUsdRate } from "entities/balance/use-usd-rate";
import { Button } from "shared/ui/button";

import { Label } from "./label";

type Props = {
  className?: string;
  style?: CSSProperties;
  currency: Currency;
  link?: string;
};

export const CardWithBalance = (props: Props) => {
  const { isConnected } = useAccount();
  const { currency, className, link, style } = props;

  const balanceQuery = useBalanceQuery({ currency });

  const usdValue = useUsdRate(currency, balanceQuery.data?.formatted);

  if (balanceQuery.isPending) {
    return (
      <div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-xl",
          className
        )}
      >
        <Skeleton
          enableAnimation={isConnected}
          width="100%"
          height={165}
          borderRadius={12}
        />
      </div>
    );
  }

  return (
    <div
      style={style}
      className={cn(
        "bg-baltic-sea/30 relative flex flex-col overflow-hidden rounded-xl",
        className
      )}
    >
      <div className="flex items-center bg-white/2 px-4 py-3.5">
        <Label size="lg" variant="normal" color="transparent" {...currency} />
      </div>

      <div className="flex flex-col pt-5.5 pb-4.5 pl-5.5">
        <span className="flex text-[32px] leading-[105%]">
          {balanceQuery.data?.formatted ?? "0"}
        </span>
        <span className="text-nobel/70 mt-2 flex">{usdValue}&nbsp;</span>
      </div>

      {link && (
        <Button
          as="a"
          href={link}
          size="34"
          className="absolute right-4.5 bottom-4.5 text-base leading-none"
          variant="spearmint"
        >
          <FaPlus className="size-3.5" /> Buy
        </Button>
      )}
    </div>
  );
};
